/**
 * Shared AuthContext creation module.
 *
 * Separated from AuthContext.tsx (the provider component) and useAuth.ts
 * (the hook) so that:
 *  - AuthContext.tsx can satisfy react-refresh (only exports a component)
 *  - useAuth.ts can satisfy react-refresh (only exports a hook)
 *  - Both share the same context object instance
 */
import { createContext } from 'react';
import type { CurrentUser, LoginRequest } from './types';

export interface AuthContextValue {
  token: string | null;
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  login: (req: LoginRequest) => Promise<void>;
  logout: () => void;
  /**
   * Mints a role-scoped JWT for the given per-role userId.
   * Calls GET /api/v1/Login/LoginV2?userId={userId}, replaces the token in
   * localStorage and React state.  Awaitable — callers can navigate after it
   * resolves.
   */
  selectRole: (userId: string | number) => Promise<void>;
  /**
   * DEV-ONLY: log in as the seeded `colt-{role}@southernstarmga.com` account
   * for the given role. Hits the real backend so the resulting JWT is
   * signature-valid (the prior client-synth approach 401'd on every
   * authenticated request). Always present in the type but only exposed by
   * AuthProvider when `import.meta.env.DEV` is true.
   */
  loginAsTestUser?: (role: import('./devLogin').TestRole) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
